import React from 'react';
import LegalDocLayout from '../../components/ChinaDolphins/LegalDocLayout/LegalDocLayout';

const TOC = [
  { id: 'operational-scope', label: '1. Operational Scope & Appointment' },
  { id: 'agent-responsibilities', label: '2. Sourcing, Vetting & Quality Control' },
  { id: 'dispute-sla', label: '3. Dispute Resolution & 72-Hour Response SLA' },
  { id: 'financial-terms', label: '4. Commissions, Milestones & Payment Settlement' },
  { id: 'anti-circumvention', label: '5. Anti-Circumvention & Non-Disintermediation' },
  { id: 'compliance-customs', label: '6. Customs, Export Compliance & Prohibited Goods' },
  { id: 'liability-termination', label: '7. Breach, Suspension & Termination' },
  { id: 'governing-law', label: '8. Governing Law & Dispute Forum' },
];

export const AgentTermsContent = () => (
  <div>
    <p>
      This Sourcing Agent Terms of Service and Platform Cooperation Agreement (&quot;Agent Agreement&quot;) is entered into by and between <strong>MTN GLOBAL LIMITED</strong> (&quot;ChinaDolphins&quot;, &quot;Platform&quot; or &quot;Company&quot;), incorporated under the laws of Hong Kong, and the verified mainland sourcing professional or agency (&quot;Sourcing Agent&quot; or &quot;Agent&quot;).
    </p>
    <p>
      By registering as an Agent, onboarding represented Factories, submitting quotations, or coordinating order execution through ChinaDolphins, the Agent agrees to be bound by the terms and conditions outlined below.
    </p>

    <h2 id="operational-scope">1. Operational Scope &amp; Appointment</h2>
    <h3>1.1 Nature of Appointment</h3>
    <p>
      ChinaDolphins authorizes the verified Sourcing Agent to act as the professional on-the-ground sourcing and verification partner for assigned Manufacturing Partners and international Buyers transacting via the Platform.
    </p>
    <h3>1.2 Non-Exclusive Relationship</h3>
    <p>
      Unless specified in a separate bilateral addendum, the appointment is non-exclusive. However, each onboarded Factory SKU is represented by one designated active Sourcing Agent within the Platform.
    </p>

    <h2 id="agent-responsibilities">2. Sourcing, Vetting &amp; Quality Control</h2>
    <p>
      The Sourcing Agent serves as the Buyer&apos;s eyes and ears on the ground in China and undertakes the following mandatory duties:
    </p>
    <ul>
      <li><strong>Factory Verification:</strong> Vetting factory business licences, production facilities, legal status, and manufacturing capabilities before listing items.</li>
      <li><strong>Accurate Product Data:</strong> Ensuring accurate product specifications, MOQ thresholds, lead times, and tiered pricing are provided.</li>
      <li><strong>Production Monitoring:</strong> Conducting active milestone monitoring of production progress and communicating real-time updates.</li>
      <li><strong>Pre-Shipment Quality Control (QC):</strong> Performing rigorous pre-shipment inspections and submitting verifiable QC reports, photos, and unboxing logs prior to dispatch.</li>
      <li><strong>Sample Coordination:</strong> Managing sample production, testing, and international courier dispatch when requested.</li>
    </ul>

    <h2 id="dispute-sla">3. Dispute Resolution &amp; 72-Hour Response SLA</h2>
    <p>
      In the event of a product dispute, damage claim, or delivery discrepancy initiated by a Buyer:
    </p>
    <ul>
      <li>The Agent is obligated to review the claim and provide an initial response with supporting evidence within <strong>72 hours</strong>.</li>
      <li>The Agent may accept the claim, propose a partial resolution, or provide counter-inspection documentation.</li>
      <li>Failure to respond within 72 hours may result in automatic arbitration findings by ChinaDolphins Administration in favor of the Buyer.</li>
    </ul>

    <h2 id="financial-terms">4. Commissions, Milestones &amp; Payment Settlement</h2>
    <p>
      Agent compensation and payout schedules are subject to milestone verification and approved third-party payment processing:
    </p>
    <ul>
      <li>Buyer payments are processed through approved third-party payment providers, and settlement proceeds are authorized in accordance with defined commercial milestones (e.g., QC approval, China Customs Export Declaration, Bill of Lading issue). ChinaDolphins does not act as a bank or escrow repository.</li>
      <li>Agent commissions are disbursed to verified corporate or commercial bank accounts via approved payment rails following administrative compliance clearance.</li>
    </ul>

    <h2 id="anti-circumvention">5. Anti-Circumvention &amp; Non-Disintermediation</h2>
    <p>
      The Agent strictly agrees not to bypass ChinaDolphins by attempting to solicit, direct, or facilitate off-platform transactions, direct payments, or private contacts (including WeChat, direct phone, private wire) with Buyers introduced through ChinaDolphins.
    </p>
    <p>
      Any confirmed circumvention activity constitutes a material breach resulting in immediate account termination, forfeiture of pending commissions, and financial liability.
    </p>

    <h2 id="compliance-customs">6. Customs, Export Compliance &amp; Prohibited Goods</h2>
    <p>
      The Agent must ensure full compliance with Chinese export laws, customs declarations, and the ChinaDolphins Global Trade &amp; AML Policy. The Agent shall never facilitate or overlook the manufacture or shipment of prohibited goods, contraband, or counterfeit items.
    </p>

    <h2 id="liability-termination">7. Breach, Suspension &amp; Termination</h2>
    <p>
      ChinaDolphins reserves the right to suspend or terminate Agent credentials upon evidence of negligence, fraudulent QC reporting, circumvention, or breach of the Global Code of Ethics.
    </p>

    <h2 id="governing-law">8. Governing Law &amp; Dispute Forum</h2>
    <p>
      This Agreement shall be construed and governed in accordance with the laws of <strong>Hong Kong</strong>. Any disputes arising hereunder that cannot be settled amicably shall be submitted to the competent arbitration centers in Hong Kong.
    </p>
  </div>
);

const AgentTermsPage = () => {
  return (
    <LegalDocLayout
      title="Terms of Service – Sourcing Agents"
      subtitle="Operational agreement, quality control standards, and compliance obligations for verified mainland Sourcing Agents."
      effectiveDate="January 1, 2026"
      lastUpdated="August 13, 2026"
      jurisdiction="Hong Kong / Mainland China (MTN GLOBAL LIMITED)"
      tocItems={TOC}
    >
      <AgentTermsContent />
    </LegalDocLayout>
  );
};

export default AgentTermsPage;
